import { useState, useEffect, useCallback } from "react";
import type { CrmMessageResponse, UnifiedContactResponse } from "../../../types/crm";
import { getContacts, createCrmContact } from "../../../services/crmContacts";
import { assignMessage } from "../../../services/crmMessages";
import { ProspectTypeahead } from "../../../components/ProspectTypeahead";
import type { ProspectOption } from "../../../components/ProspectTypeahead";
import { CrmTeamTypeahead } from "../../../components/CrmTeamTypeahead";
import type { CrmTeamOption } from "../../../components/CrmTeamTypeahead";
import { SelectPopover } from "../../../components/SelectPopover";
import { ComboboxPopover } from "../../../components/ComboboxPopover";
import { MultiStateSelect } from "../../../components/MultiStateSelect";
import { POSITION_OPTIONS } from "../../../constants/positions";
import { US_STATES } from "../../../constants/usStates";
import {
  formatPhoneNumber,
  stripPhoneFormat,
  formatHeight,
} from "../../../utils/formatters";

interface Props {
  message: CrmMessageResponse;
  onClose: () => void;
  onAssigned: () => void;
}

type ModalMode = "assign" | "create";
type CreateContactType = "Coach" | "Parent" | "ProspectProfile";

function extractIdentifier(raw: string): string {
  const match = raw.match(/<([^>]+)>/);
  return match ? match[1].trim() : raw.trim();
}

export const AssignMessageModal = ({ message, onClose, onAssigned }: Props) => {
  // Mode
  const [mode, setMode] = useState<ModalMode>("assign");

  // Mode A: assign existing
  const [search, setSearch] = useState("");
  const [contacts, setContacts] = useState<UnifiedContactResponse[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedContact, setSelectedContact] = useState<UnifiedContactResponse | null>(null);
  const [assigning, setAssigning] = useState(false);

  // Mode B: create new — starts as null until user selects a type
  const [createType, setCreateType] = useState<CreateContactType | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [title, setTitle] = useState("");
  const [gradYear, setGradYear] = useState("");
  const [position, setPosition] = useState("");
  const [xHandle, setXHandle] = useState("");
  const [slackUsername, setSlackUsername] = useState("");
  const [preferredContactMethod, setPreferredContactMethod] = useState("");
  const [selectedProspect, setSelectedProspect] = useState<ProspectOption | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<CrmTeamOption | null>(null);
  const [creating, setCreating] = useState(false);

  // ProspectProfile expanded fields
  const [bats, setBats] = useState("");
  const [throws_, setThrows] = useState("");
  const [heightFeet, setHeightFeet] = useState("");
  const [heightInches, setHeightInches] = useState("");
  const [weight, setWeight] = useState("");
  const [school, setSchool] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [gpa, setGpa] = useState("");
  const [intendedMajor, setIntendedMajor] = useState("");
  const [preferredLocations, setPreferredLocations] = useState<string[]>([]);
  const [videoLink, setVideoLink] = useState("");
  const [scoutingProfileLink, setScoutingProfileLink] = useState("");
  const [travelTeam, setTravelTeam] = useState("");
  const [travelTeamCoachName, setTravelTeamCoachName] = useState("");
  const [travelTeamCoachPhone, setTravelTeamCoachPhone] = useState("");

  // Shared
  const [error, setError] = useState("");

  const prefillChannelField = useCallback(() => {
    if (!message.senderIdentifier) return;
    const raw = message.senderIdentifier;
    const ch = message.channel?.toLowerCase();
    if (ch === "gmail") setEmail(extractIdentifier(raw));
    else if (ch === "quo") setPhone(extractIdentifier(raw));
    else if (ch === "x" || ch === "twitter") setXHandle(extractIdentifier(raw));
    else if (ch === "slack") setSlackUsername(extractIdentifier(raw));
  }, [message]);

  // Re-apply prefill when type changes in create mode, clearing incompatible fields first
  useEffect(() => {
    if (mode !== "create" || createType === null) return;
    setEmail("");
    setPhone("");
    setXHandle("");
    setSlackUsername("");
    prefillChannelField();
    // Coach has no Slack channel — clear slackUsername if Slack message
    if (message.channel?.toLowerCase() === "slack" && createType === "Coach") {
      setSlackUsername("");
    }
  }, [createType, mode, prefillChannelField, message.channel]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onClose]);

  const searchContacts = useCallback(async (q: string) => {
    if (!q.trim()) {
      setContacts([]);
      return;
    }
    setSearching(true);
    try {
      const data = await getContacts({ search: q, pageSize: 10 });
      setContacts(data.items);
    } catch {
      // non-critical
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => searchContacts(search), 300);
    return () => clearTimeout(timer);
  }, [search, searchContacts]);

  const resetProspectFields = () => {
    setBats(""); setThrows(""); setHeightFeet(""); setHeightInches("");
    setWeight(""); setSchool(""); setCity(""); setState("");
    setGpa(""); setIntendedMajor(""); setPreferredLocations([]);
    setVideoLink(""); setScoutingProfileLink("");
    setTravelTeam(""); setTravelTeamCoachName(""); setTravelTeamCoachPhone("");
  };

  const switchToAssign = () => {
    setMode("assign");
    setCreateType(null);
    setFirstName("");
    setLastName("");
    setEmail("");
    setPhone("");
    setTitle("");
    setGradYear("");
    setPosition("");
    setXHandle("");
    setSlackUsername("");
    setPreferredContactMethod("");
    setSelectedProspect(null);
    setSelectedTeam(null);
    resetProspectFields();
    setError("");
  };

  const switchToCreate = () => {
    setMode("create");
    setSearch("");
    setContacts([]);
    setSelectedContact(null);
    setError("");
  };

  const handleAssign = async () => {
    if (!selectedContact) return;
    setAssigning(true);
    setError("");
    try {
      await assignMessage(message.id, {
        contactType: selectedContact.contactType,
        contactId: selectedContact.id,
        updateChannelIdentifier: true,
      });
      onAssigned();
    } catch {
      setError("Failed to assign message.");
      setAssigning(false);
    }
  };

  const isFormValid = (): boolean => {
    if (!createType) return false;
    if (!firstName.trim() || !lastName.trim()) return false;

    if (createType === "ProspectProfile") {
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return false;
      if (!phone.trim() || stripPhoneFormat(phone).length !== 10) return false;
      if (!position || !bats || !throws_) return false;
      if (!heightFeet.trim() || !weight.trim()) return false;
      if (!school.trim() || !city.trim() || !state) return false;
      if (!gradYear.trim()) return false;
      if (preferredLocations.length === 0) return false;
    }

    if (createType === "Coach") {
      if (!title.trim()) return false;
      if (!email.trim() && !phone.trim()) return false;
    }

    if (createType === "Parent") {
      if (!selectedProspect) return false;
      if (!email.trim() && !phone.trim()) return false;
    }

    return true;
  };

  const validateCreate = (): string | null => {
    if (!firstName.trim() || !lastName.trim())
      return "First and last name are required.";

    if (createType === "ProspectProfile") {
      if (!email.trim()) return "Email is required.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return "Invalid email format.";
      if (!phone.trim()) return "Phone is required.";
      if (stripPhoneFormat(phone).length !== 10) return "Phone must be 10 digits.";
      if (!position) return "Position is required.";
      if (!bats) return "Bats is required.";
      if (!throws_) return "Throws is required.";
      if (!heightFeet.trim()) return "Height is required.";
      if (!weight.trim()) return "Weight is required.";
      if (!school.trim()) return "School is required.";
      if (!city.trim()) return "City is required.";
      if (!state) return "State is required.";
      if (!gradYear.trim()) return "Class is required.";
      if (preferredLocations.length === 0) return "Select at least 1 preferred location.";
    }

    if (createType === "Coach") {
      if (!title.trim()) return "Title / Role is required for coaches.";
      if (!email.trim() && !phone.trim()) return "Email or phone is required.";
    }

    if (createType === "Parent") {
      if (!email.trim() && !phone.trim()) return "Email or phone is required.";
      if (!selectedProspect) return "A related prospect is required for parents.";
    }

    return null;
  };

  const handleCreateAndAssign = async () => {
    if (!createType) return;
    const validationError = validateCreate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setCreating(true);
    setError("");
    let newContactId: string | null = null;
    let newContactType: string | null = null;
    try {
      const newContact = await createCrmContact({
        contactType: createType,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
        phone: stripPhoneFormat(phone) || undefined,
        ...(createType === "Coach" && {
          roleTitle: title.trim(),
          xHandle: xHandle.trim() || undefined,
          preferredContactMethod: preferredContactMethod || undefined,
          crmTeamId: selectedTeam?.id || undefined,
        }),
        ...(createType === "ProspectProfile" && {
          gradYear: gradYear.trim() || undefined,
          position: position || undefined,
          bats: bats || undefined,
          throws: throws_ || undefined,
          height: formatHeight(heightFeet, heightInches) || undefined,
          weight: weight ? parseInt(weight, 10) : undefined,
          school: school.trim() || undefined,
          city: city.trim() || undefined,
          state: state || undefined,
          gpa: gpa.trim() || undefined,
          intendedMajor: intendedMajor.trim() || undefined,
          preferredLocations: preferredLocations.length > 0 ? preferredLocations : undefined,
          videoLink: videoLink.trim() || undefined,
          scoutingProfileLink: scoutingProfileLink.trim() || undefined,
          travelTeam: travelTeam.trim() || undefined,
          travelTeamCoachName: travelTeamCoachName.trim() || undefined,
          travelTeamCoachPhone: stripPhoneFormat(travelTeamCoachPhone) || undefined,
          xHandle: xHandle.trim() || undefined,
          slackUsername: slackUsername.trim() || undefined,
        }),
        ...(createType === "Parent" && {
          prospectId: selectedProspect?.id,
          xHandle: xHandle.trim() || undefined,
          slackUsername: slackUsername.trim() || undefined,
        }),
      });
      newContactId = newContact.id;
      newContactType = newContact.contactType;
      await assignMessage(message.id, {
        contactType: newContact.contactType,
        contactId: newContact.id,
        updateChannelIdentifier: false,
      });
      onAssigned();
    } catch {
      if (newContactId && newContactType) {
        setError(
          "Contact was created but could not be assigned to this message. Find them in Contacts to assign manually."
        );
      } else {
        setError("Failed to create contact and assign message.");
      }
      setCreating(false);
    }
  };

  const contactTypeLabel = (ct: string) =>
    ct === "ProspectProfile" ? "Player" : ct;

  const isWide = mode === "create" && createType === "ProspectProfile";

  return (
    <div className="assign-modal__overlay" onClick={onClose}>
      <div className={`assign-modal${isWide ? " assign-modal--wide" : ""}`} onClick={(e) => e.stopPropagation()}>
        <div className="assign-modal__header">
          <h3 className="assign-modal__title">Assign Message</h3>
          <button className="assign-modal__close" onClick={onClose}>×</button>
        </div>

        <div className="assign-modal__tabs">
          <button
            className={`assign-modal__tab${mode === "assign" ? " assign-modal__tab--active" : ""}`}
            onClick={switchToAssign}
          >
            Assign Existing
          </button>
          <button
            className={`assign-modal__tab${mode === "create" ? " assign-modal__tab--active" : ""}`}
            onClick={switchToCreate}
          >
            Create New
          </button>
        </div>

        {error && <div className="assign-modal__error">{error}</div>}

        {mode === "assign" && (
          <>
            <input
              className="assign-modal__search"
              placeholder="Search contacts..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedContact(null);
              }}
              autoFocus
            />
            {searching && (
              <div className="assign-modal__searching">Searching...</div>
            )}
            {contacts.length > 0 && (
              <div className="assign-modal__results">
                {contacts.map((c) => (
                  <button
                    key={c.id}
                    className={`assign-modal__result${selectedContact?.id === c.id ? " assign-modal__result--selected" : ""}`}
                    onClick={() => setSelectedContact(c)}
                  >
                    <span className="assign-modal__result-name">{c.displayName}</span>
                    <span className="assign-modal__result-meta">
                      {contactTypeLabel(c.contactType)}
                      {c.email && ` · ${c.email}`}
                      {c.phone && ` · ${c.phone}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
            {selectedContact && (
              <div className="assign-modal__warning-banner">
                Assigning this message to this entity will override the contact
                information they have for this platform.
              </div>
            )}
            <button
              className="assign-modal__submit"
              disabled={!selectedContact || assigning}
              onClick={handleAssign}
            >
              {assigning ? "Assigning..." : "Assign"}
            </button>
          </>
        )}

        {mode === "create" && (
          <div className="assign-modal__create-form">
            <div className="assign-modal__type-row">
              {(["Coach", "Parent", "ProspectProfile"] as CreateContactType[]).map((t) => (
                <button
                  key={t}
                  className={`assign-modal__type-btn${createType === t ? " assign-modal__type-btn--active" : ""}`}
                  onClick={() => setCreateType(t)}
                >
                  {t === "ProspectProfile" ? "Player" : t}
                </button>
              ))}
            </div>

            {createType === null ? (
              <p className="assign-modal__type-prompt">
                Select a contact type to continue.
              </p>
            ) : (
              <>
                {/* PERSONAL INFORMATION — shared name/email/phone */}
                {createType === "ProspectProfile" && (
                  <div className="assign-modal__section-header">Personal Information</div>
                )}

                <div className="assign-modal__row">
                  <div className="assign-modal__field">
                    <label htmlFor="assign-message-modal-first-name-0" className="assign-modal__label">First Name *</label>
                    <input id="assign-message-modal-first-name-0"
                      className="assign-modal__input"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="assign-modal__field">
                    <label htmlFor="assign-message-modal-last-name-1" className="assign-modal__label">Last Name *</label>
                    <input id="assign-message-modal-last-name-1"
                      className="assign-modal__input"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                <div className="assign-modal__row">
                  <div className="assign-modal__field">
                    <label htmlFor="assign-message-modal-email-2" className="assign-modal__label">Email *</label>
                    <input id="assign-message-modal-email-2"
                      className="assign-modal__input"
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (!e.target.value && preferredContactMethod === "Gmail")
                          setPreferredContactMethod("");
                      }}
                    />
                  </div>
                  <div className="assign-modal__field">
                    <label htmlFor="assign-message-modal-phone-3" className="assign-modal__label">Phone *</label>
                    <input id="assign-message-modal-phone-3"
                      className="assign-modal__input"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={phone}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        setPhone(formatted);
                        if (!e.target.value && preferredContactMethod === "Quo")
                          setPreferredContactMethod("");
                      }}
                    />
                  </div>
                </div>

                {/* === COACH === */}
                {createType === "Coach" && (
                  <>
                    <div className="assign-modal__field">
                      <label htmlFor="assign-message-modal-title-role-4" className="assign-modal__label">Title / Role *</label>
                      <select id="assign-message-modal-title-role-4"
                        className="assign-modal__select"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                      >
                        <option value="">Select...</option>
                        <option value="Head Coach">Head Coach</option>
                        <option value="Recruiting Coordinator">Recruiting Coordinator</option>
                        <option value="Assistant Coach">Assistant Coach</option>
                      </select>
                    </div>
                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-x-handle-optional-5" className="assign-modal__label">X Handle (optional)</label>
                        <input id="assign-message-modal-x-handle-optional-5"
                          className="assign-modal__input"
                          placeholder="@handle"
                          value={xHandle}
                          onChange={(e) => {
                            setXHandle(e.target.value);
                            if (!e.target.value && preferredContactMethod === "X")
                              setPreferredContactMethod("");
                          }}
                        />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-preferred-contact-6" className="assign-modal__label">Preferred Contact</label>
                        <select id="assign-message-modal-preferred-contact-6"
                          className="assign-modal__select"
                          value={preferredContactMethod}
                          onChange={(e) => setPreferredContactMethod(e.target.value)}
                        >
                          <option value="">Select...</option>
                          <option value="Gmail" disabled={!email}>Gmail</option>
                          <option value="Quo" disabled={!phone}>Phone</option>
                          <option value="X" disabled={!xHandle}>X</option>
                        </select>
                      </div>
                    </div>
                    <div className="assign-modal__field assign-modal__field--typeahead">
                      <label htmlFor="assign-message-modal-team-optional-0" className="assign-modal__label">Team (optional)</label>
                      <CrmTeamTypeahead inputId="assign-message-modal-team-optional-0"
                        value={selectedTeam}
                        onSelect={setSelectedTeam}
                        placeholder="Search teams..."
                      />
                    </div>
                  </>
                )}

                {/* === PROSPECT PROFILE (PLAYER) — full form === */}
                {createType === "ProspectProfile" && (
                  <>
                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-x-handle-7" className="assign-modal__label">X Handle</label>
                        <input id="assign-message-modal-x-handle-7"
                          className="assign-modal__input"
                          placeholder="@handle"
                          value={xHandle}
                          onChange={(e) => setXHandle(e.target.value)}
                        />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-slack-username-8" className="assign-modal__label">Slack Username</label>
                        <input id="assign-message-modal-slack-username-8"
                          className="assign-modal__input"
                          placeholder="username or email"
                          value={slackUsername}
                          onChange={(e) => setSlackUsername(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* LINKS */}
                    <div className="assign-modal__section-header">Links</div>
                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-video-link-9" className="assign-modal__label">Video Link</label>
                        <input id="assign-message-modal-video-link-9" className="assign-modal__input" placeholder="https://..." value={videoLink} onChange={(e) => setVideoLink(e.target.value)} />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-scouting-profile-link-10" className="assign-modal__label">Scouting Profile Link</label>
                        <input id="assign-message-modal-scouting-profile-link-10" className="assign-modal__input" placeholder="https://..." value={scoutingProfileLink} onChange={(e) => setScoutingProfileLink(e.target.value)} />
                      </div>
                    </div>

                    {/* PLAYER DETAILS */}
                    <div className="assign-modal__section-header">Player Details</div>
                    <div className="assign-modal__field">
                      <label htmlFor="assign-message-modal-main-position-0" className="assign-modal__label">Main Position *</label>
                      <SelectPopover id="assign-message-modal-main-position-0" options={POSITION_OPTIONS} value={position} onChange={setPosition} placeholder="Select position..." fullWidth />
                    </div>

                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-bats-1" className="assign-modal__label">Bats *</label>
                        <SelectPopover id="assign-message-modal-bats-1" options={["L", "R"]} value={bats} onChange={setBats} placeholder="L / R" fullWidth />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-throws-2" className="assign-modal__label">Throws *</label>
                        <SelectPopover id="assign-message-modal-throws-2" options={["L", "R"]} value={throws_} onChange={setThrows} placeholder="L / R" fullWidth />
                      </div>
                    </div>

                    <div className="assign-modal__row assign-modal__row--three">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-height-ft-11" className="assign-modal__label">Height (ft) *</label>
                        <input id="assign-message-modal-height-ft-11" className="assign-modal__input" type="number" placeholder="6" value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)} />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-height-in-12" className="assign-modal__label">Height (in)</label>
                        <input id="assign-message-modal-height-in-12" className="assign-modal__input" type="number" placeholder="1" value={heightInches} onChange={(e) => setHeightInches(e.target.value)} />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-weight-lbs-13" className="assign-modal__label">Weight (lbs) *</label>
                        <input id="assign-message-modal-weight-lbs-13" className="assign-modal__input" type="number" placeholder="185" value={weight} onChange={(e) => setWeight(e.target.value)} />
                      </div>
                    </div>

                    {/* ACADEMIC & LOCATION */}
                    <div className="assign-modal__section-header">Academic & Location</div>
                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-school-14" className="assign-modal__label">School *</label>
                        <input id="assign-message-modal-school-14" className="assign-modal__input" value={school} onChange={(e) => setSchool(e.target.value)} />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-class-15" className="assign-modal__label">Class *</label>
                        <input id="assign-message-modal-class-15" className="assign-modal__input" type="number" placeholder="2026" value={gradYear} onChange={(e) => setGradYear(e.target.value)} />
                      </div>
                    </div>

                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-city-16" className="assign-modal__label">City *</label>
                        <input id="assign-message-modal-city-16" className="assign-modal__input" placeholder="City" value={city} onChange={(e) => setCity(e.target.value)} />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-state-3" className="assign-modal__label">State *</label>
                        <ComboboxPopover id="assign-message-modal-state-3" options={US_STATES} value={state} onChange={setState} placeholder="State" transform={(v) => v.toUpperCase().slice(0, 2)} fullWidth />
                      </div>
                    </div>

                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-gpa-17" className="assign-modal__label">GPA</label>
                        <input id="assign-message-modal-gpa-17" className="assign-modal__input" placeholder="3.5" value={gpa} onChange={(e) => setGpa(e.target.value)} />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-intended-major-18" className="assign-modal__label">Intended Major</label>
                        <input id="assign-message-modal-intended-major-18" className="assign-modal__input" value={intendedMajor} onChange={(e) => setIntendedMajor(e.target.value)} />
                      </div>
                    </div>

                    <MultiStateSelect
                      label="Preferred Location *"
                      value={preferredLocations}
                      onChange={setPreferredLocations}
                      max={3}
                    />

                    {/* TRAVEL TEAM */}
                    <div className="assign-modal__section-header">Travel Team</div>
                    <div className="assign-modal__field">
                      <label htmlFor="assign-message-modal-travel-team-name-19" className="assign-modal__label">Travel Team Name</label>
                      <input id="assign-message-modal-travel-team-name-19" className="assign-modal__input" value={travelTeam} onChange={(e) => setTravelTeam(e.target.value)} />
                    </div>
                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-coach-name-20" className="assign-modal__label">Coach Name</label>
                        <input id="assign-message-modal-coach-name-20" className="assign-modal__input" value={travelTeamCoachName} onChange={(e) => setTravelTeamCoachName(e.target.value)} />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-coach-phone-21" className="assign-modal__label">Coach Phone</label>
                        <input id="assign-message-modal-coach-phone-21" className="assign-modal__input" type="tel" placeholder="(555) 123-4567" value={travelTeamCoachPhone} onChange={(e) => setTravelTeamCoachPhone(formatPhoneNumber(e.target.value))} />
                      </div>
                    </div>
                  </>
                )}

                {/* === PARENT === */}
                {createType === "Parent" && (
                  <>
                    <div className="assign-modal__field assign-modal__field--typeahead">
                      <label htmlFor="assign-message-modal-related-prospect-1" className="assign-modal__label">Related Prospect *</label>
                      <ProspectTypeahead inputId="assign-message-modal-related-prospect-1"
                        value={selectedProspect}
                        onSelect={setSelectedProspect}
                        placeholder="Search by prospect name..."
                      />
                    </div>
                    <div className="assign-modal__row">
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-x-username-optional-22" className="assign-modal__label">X Username (optional)</label>
                        <input id="assign-message-modal-x-username-optional-22"
                          className="assign-modal__input"
                          placeholder="@handle"
                          value={xHandle}
                          onChange={(e) => setXHandle(e.target.value)}
                        />
                      </div>
                      <div className="assign-modal__field">
                        <label htmlFor="assign-message-modal-slack-username-optional-23" className="assign-modal__label">Slack Username (optional)</label>
                        <input id="assign-message-modal-slack-username-optional-23"
                          className="assign-modal__input"
                          placeholder="username or email"
                          value={slackUsername}
                          onChange={(e) => setSlackUsername(e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}

                <button
                  className="assign-modal__submit"
                  disabled={!isFormValid() || creating}
                  onClick={handleCreateAndAssign}
                >
                  {creating ? "Creating..." : "Create & Assign"}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
